import React from "react";
import { PropTypes } from "prop-types";
import { dateFormatter, currencyFormatter } from "mrb/common/formatting";
import { MrbButton } from "mrb/components/button";
import {
    PremiumTrialTermsInfo,
    SubscriptionPrice,
    InvalidPaymentInfo,
    NakiRadioPremiumBadge,
    NakiNigunPremiumBadge,
    NakiRadioFreeBadge,
} from "application/payment/components";
import { MrbRouterLink } from "mrb/core/infrastructure/router";
import {
    applicationDefaults,
    nakiRadioUserAccessType,
    subscriptionPlans,
    subscriptionStatuses,
} from "common/constants";
import { isSubscriptionBundle } from "common/utils";
import { observer } from "mobx-react";
import { isNil } from "lodash";
import { mergeCss } from "mrb/common/utils";
import moment from "moment";

function UserSubscriptionInfoTemplate({ userSubscriptionsPreviewViewStore, t }) {
    const {
        activeSubscription,
        pendingSubscription,
        isSubscriptionBundleActive,
        rootStore: { premiumSubscriptionStore },
    } = userSubscriptionsPreviewViewStore;
    return (
        <div>
            <div className="c-payment__list c-payment__list--plan">
                <SubscriptionHeader
                    subscription={activeSubscription}
                    userAccessType={premiumSubscriptionStore.nakiRadioUserAccessType}
                />
                {pendingSubscription && !isSubscriptionBundleActive ? (
                    <React.Fragment>
                        <div className="u-display--flex u-display--flex--ai--center u-mar--bottom--med">
                            <i className="u-icon u-icon--lrg u-icon--arrow-right--secondary"></i>
                        </div>
                        <SubscriptionHeader subscription={pendingSubscription} />
                    </React.Fragment>
                ) : null}
            </div>
            <MyPlansCouponField store={userSubscriptionsPreviewViewStore} t={t} />
            <SubscriptionDetails store={userSubscriptionsPreviewViewStore} t={t} />
        </div>
    );
}

const MyPlansCouponField = observer(function MyPlansCouponField({ store, t }) {
    if (!store.showCouponField) {
        return null;
    }
    const { couponCode, isApplyingCoupon, couponErrorMessageKey } = store;
    return (
        <div className="c-payment__card c-payment__card--full u-mar--bottom--med">
            <label className="u-type--sml u-display--block u-mar--bottom--tny">
                {t("APPLICATION.PREMIUM.PAYMENT.COUPON.LABEL")}
            </label>
            <div className="u-display--flex">
                <input
                    type="text"
                    className="c-input c-input--text c-input--med u-mar--right--sml"
                    placeholder={t("APPLICATION.PREMIUM.PAYMENT.COUPON.PLACEHOLDER")}
                    value={couponCode}
                    onChange={(e) => store.onChangeCouponCode(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            store.applyCoupon();
                        }
                    }}
                />
                <button
                    type="button"
                    className="c-btn c-btn--med c-btn--primary"
                    disabled={isApplyingCoupon || !(couponCode && couponCode.trim())}
                    onClick={store.applyCoupon}
                >
                    {t("APPLICATION.PREMIUM.PAYMENT.COUPON.APPLY")}
                </button>
            </div>
            {couponErrorMessageKey && (
                <p className="u-type--sml u-type--color--warning u-mar--top--tny">{t(couponErrorMessageKey)}</p>
            )}
        </div>
    );
});

function SubscriptionHeader({ subscription, userAccessType }) {
    let premiumBadge = null;
    let activeClassName = "";
    switch (subscription.product.slug) {
        case subscriptionPlans.nakiRadioPremium:
        case subscriptionPlans.nakiOne:
            premiumBadge = <NakiRadioPremiumBadge iconSize="base" userAccessType={userAccessType} />;
            activeClassName = "primary";
            break;
        case subscriptionPlans.nakiNigun:
            premiumBadge = <NakiNigunPremiumBadge iconSize="base" />;
            activeClassName = "tertiary";
            break;
        default:
            premiumBadge = <NakiRadioFreeBadge iconSize="base" />;
            activeClassName = "secondary";
            break;
    }

    let subscriptionStatus = "";
    let isTrial = false;
    switch (subscription.subscriptionStatus.abrv) {
        case subscriptionStatuses.pastDue:
            subscriptionStatus = "tertiary";
            break;
        case subscriptionStatuses.active:
            subscriptionStatus = "success";
            break;
        case subscriptionStatuses.trial:
            isTrial = true;
            subscriptionStatus = "secondary";
            break;
        default:
            break;
    }

    const coupon = buildCouponDisplay(subscription);

    return (
        <div className={`c-payment__card c-payment__card--full c-payment__card--${activeClassName} u-mar--bottom--med`}>
            <div className="u-display--flex u-display--flex--ai--center">
                {premiumBadge}

                <div className="u-mar--left--med">
                    <p>{subscription.product.name}</p>
                    <p className="u-type--med u-type--wgt--bold u-mar--top--tny">
                        {coupon ? (
                            <span>{coupon.primary}</span>
                        ) : isTrial || subscription.amount === 0 ? (
                            <span>Free</span>
                        ) : (
                            <SubscriptionPrice price={subscription.amount} />
                        )}
                    </p>
                    {coupon && coupon.renewal && <p className="u-type--sml u-type--color--note">{coupon.renewal}</p>}
                    <p className={mergeCss("u-type--sml u-type--wgt--bold", `u-type--color--${subscriptionStatus}`)}>
                        {subscription.subscriptionStatus.name}
                        {coupon && <span className="u-type--color--success"> &bull; Coupon applied</span>}
                    </p>
                </div>
            </div>
        </div>
    );
}

// Builds the discount-aware price text for a subscription with an active coupon, or null when no
// discount is present. `discount` comes from the backend (amountOff in dollars, percentOff as a whole
// number, activeTo = Stripe discount end date). The renewal price always reflects the plan's full price.
function buildCouponDisplay(subscription) {
    const discount = subscription.discount;
    const planPrice = subscription.product ? subscription.product.price : null;
    if (isNil(discount) || isNil(planPrice)) {
        return null;
    }

    const formatPrice = (value) => currencyFormatter.format(value, { currency: "USD" });

    let discountedCharge;
    if (!isNil(discount.amountOff)) {
        discountedCharge = Math.max(Math.round((planPrice - discount.amountOff) * 100) / 100, 0);
    } else if (!isNil(discount.percentOff)) {
        discountedCharge = Math.max(Math.round(planPrice * (1 - discount.percentOff / 100) * 100) / 100, 0);
    } else {
        return null;
    }

    // When the subscription is set to cancel it ends on endDate (before the coupon's natural
    // end) and won't renew, so reflect the cancellation date instead of the coupon's far-off date.
    const isCanceled = !isNil(subscription.endDate);
    const untilSource = isCanceled ? subscription.endDate : discount.activeTo;
    const activeTo = untilSource
        ? dateFormatter.format(untilSource, { format: applicationDefaults.formats.date })
        : null;

    let primary;
    if (discountedCharge === 0) {
        primary = activeTo ? `Free until ${activeTo}` : "First month free";
    } else if (activeTo) {
        primary = `${formatPrice(discountedCharge)}/month until ${activeTo}`;
    } else {
        primary = `${formatPrice(discountedCharge)} first month`;
    }

    const renewal = isCanceled ? null : `then ${formatPrice(planPrice)}/month`;
    return { primary, renewal };
}

const SubscriptionDetails = observer(function SubscriptionDetails({ store, t }) {
    const {
        activeSubscription,
        pendingSubscription,
        rootStore: { premiumSubscriptionStore },
    } = store;
    const isPremiumSubscriptionTrial = activeSubscription.subscriptionStatus.abrv === subscriptionStatuses.trial;

    return (
        <div className="c-payment__card c-payment__card--full">
            {activeSubscription.product.productCategory &&
            isSubscriptionBundle(activeSubscription.product.productCategory) &&
            activeSubscription.currentBillingCycle < 12 ? (
                <React.Fragment>
                    <DefaultSubscriptionDetails subscription={activeSubscription} />
                    <div className="u-separator--primary"></div>
                    <NakiBundleSubscriptionDetails activeSubscription={activeSubscription} />
                </React.Fragment>
            ) : (
                <React.Fragment>
                    {isNil(pendingSubscription) ? (
                        <DefaultSubscriptionDetails subscription={activeSubscription} />
                    ) : (
                        <UpgradeSubscriptionDetails
                            activeSubscription={activeSubscription}
                            pendingSubscription={pendingSubscription}
                            isSubscriptionBundleActive={premiumSubscriptionStore.isSubscriptionBundleActive}
                        />
                    )}
                </React.Fragment>
            )}
            {!premiumSubscriptionStore.isSubscriptionBundleActive && <div className="u-separator--primary"></div>}

            {isPremiumSubscriptionTrial && <PremiumTrialTermsInfo subscription={activeSubscription} t={t} />}
            {activeSubscription.latestInvalidPaymentTransaction &&
                activeSubscription.latestInvalidPaymentTransaction.error && (
                    <InvalidPaymentInfo
                        subscription={activeSubscription}
                        paymentError={activeSubscription.latestInvalidPaymentTransaction.error}
                        t={t}
                    />
                )}
            <div className="u-mar--top--tny">
                {activeSubscription.isInternal ? (
                    <InternalSubscriptionControls store={store} t={t} />
                ) : (
                    <SubscriptionControls store={store} t={t} />
                )}
            </div>
        </div>
    );
});

UserSubscriptionInfoTemplate.propTypes = {
    userSubscriptionsPreviewViewStore: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

function DefaultSubscriptionDetails({ subscription }) {
    const isPremiumSubscriptionTrial = subscription.subscriptionStatus.abrv === subscriptionStatuses.trial;

    return (
        <React.Fragment>
            {subscription.startDate && (
                <div className="u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between u-mar--bottom--tny">
                    <span className="u-type--base">Start Date:</span>

                    <p>
                        {dateFormatter.format(subscription.startDate, {
                            format: applicationDefaults.formats.date,
                        })}
                    </p>
                </div>
            )}
            <div className="u-display--flex u-display--flex--ai--center u-display--flex--jc--space-between u-mar--bottom--med">
                {isNil(subscription.endDate) ? (
                    <React.Fragment>
                        {!isNil(subscription.nextBillingDate) && (
                            <React.Fragment>
                                <span className="u-type--base">
                                    {isPremiumSubscriptionTrial ? "Start Billing Date:" : "Next Billing Date:"}
                                </span>
                                <p>
                                    {dateFormatter.format(subscription.nextBillingDate, {
                                        format: applicationDefaults.formats.date,
                                    })}
                                </p>
                            </React.Fragment>
                        )}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <span className="u-type--base">End Date:</span>
                        <p>
                            {dateFormatter.format(subscription.endDate, {
                                format: applicationDefaults.formats.date,
                            })}
                        </p>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );
}

const NakiBundleSubscriptionDetails = observer(function NakiBundleSubscriptionDetails({ activeSubscription }) {
    return (
        <React.Fragment>
            <p className="u-mar--bottom--sml">
                After{" "}
                {dateFormatter.format(moment(activeSubscription.startDate).add(12, "M"), {
                    format: applicationDefaults.formats.date,
                })}
                , your subscription may be cancelled at any time. For technical support and device repair/replacement,
                please contact our support team at <a href="mailto: support@nakiradio.com">support@nakiradio.com</a>.
            </p>
        </React.Fragment>
    );
});

const UpgradeSubscriptionDetails = observer(function UpgradeSubscriptionDetails({
    activeSubscription,
    pendingSubscription,
    isSubscriptionBundleActive,
}) {
    return (
        <React.Fragment>
            <p className="u-mar--bottom--sml">
                Your <strong>{activeSubscription.product.name}</strong> subscription will remain active until{" "}
                <strong>
                    {isSubscriptionBundleActive
                        ? activeSubscription.endDate
                            ? dateFormatter.format(activeSubscription.endDate, {
                                  format: applicationDefaults.formats.date,
                              })
                            : null
                        : activeSubscription.endDate
                        ? dateFormatter.format(activeSubscription.nextBillingDate, {
                              format: applicationDefaults.formats.date,
                          })
                        : null}
                </strong>
                .
            </p>
            <p className="u-mar--bottom--sml">
                Your <strong>{pendingSubscription.product.name}</strong> subscription starts on{" "}
                <strong>
                    {pendingSubscription.startDate
                        ? dateFormatter.format(pendingSubscription.startDate, {
                              format: applicationDefaults.formats.date,
                          })
                        : null}
                </strong>
                , and we will charge you{" "}
                <strong>
                    <SubscriptionPrice price={pendingSubscription.amount} />
                </strong>
                .
            </p>
        </React.Fragment>
    );
});

const InternalSubscriptionControls = observer(function InternalSubscriptionControls({ store, t }) {
    const { cancelSubscription } = store;

    return (
        <div>
            <MrbButton
                classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
                label="Cancel Subscription"
                onClick={cancelSubscription}
            />{" "}
        </div>
    );
});

const SubscriptionControls = observer(function SubscriptionControls({ store, t }) {
    const {
        activeSubscription,
        pendingSubscription,
        cancelSubscription,
        renewSubscription,
        revertSubscription,
        endTrial,
        rootStore: { premiumSubscriptionStore },
    } = store;
    const isPremiumSubscriptionTrial = activeSubscription.subscriptionStatus.abrv === subscriptionStatuses.trial;

    if (premiumSubscriptionStore.isSubscriptionBundleActive) {
        if (activeSubscription.currentBillingCycle >= 12) {
            return (
                <div>
                    <MrbButton
                        classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
                        label="Cancel Subscription"
                        onClick={cancelSubscription}
                    />{" "}
                </div>
            );
        } else {
            return null;
        }
    }

    if (activeSubscription.product.slug === subscriptionPlans.nakiRadioFree) {
        return (
            <div>
                <MrbRouterLink
                    className="c-btn c-btn--med c-btn--full c-btn--ghost"
                    routeName="master.application.premium.subscriptions"
                >
                    Upgrade Plan
                </MrbRouterLink>
            </div>
        );
    }

    if (isPremiumSubscriptionTrial) {
        if (isNil(activeSubscription.endDate)) {
            return (
                <div>
                    <MrbButton
                        classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
                        label="Cancel Subscription"
                        onClick={cancelSubscription}
                    />{" "}
                </div>
            );
        }
        return (
            <div>
                <MrbButton
                    classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
                    label="End Trial"
                    onClick={() => endTrial(activeSubscription.id)}
                />
            </div>
        );
    }

    if (isNil(activeSubscription.endDate)) {
        return (
            <div>
                {premiumSubscriptionStore.nakiRadioUserAccessType === nakiRadioUserAccessType.free && (
                    <MrbRouterLink
                        className="c-btn c-btn--med c-btn--full c-btn--primary u-mar--bottom--sml"
                        routeName="master.application.premium.subscriptions"
                    >
                        Change Plan
                    </MrbRouterLink>
                )}

                <MrbButton
                    classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
                    label="Cancel Subscription"
                    onClick={cancelSubscription}
                />
            </div>
        );
    } else if (!isNil(pendingSubscription)) {
        return (
            <MrbButton
                classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
                label={`Revert to ${activeSubscription.product.name}`}
                onClick={revertSubscription}
            />
        );
    }

    return (
        <MrbButton
            classNameOverride="c-btn c-btn--med c-btn--full c-btn--ghost"
            label="Renew Subscription"
            onClick={renewSubscription}
        />
    );
});

export default observer(UserSubscriptionInfoTemplate);
